import { AlertTriangle } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';

import { sessionState } from '@chainlit/react-client';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';

import { rewindConfirmState } from '@/state/project';

export default function RewindConfirmDialog() {
  const session = useRecoilValue(sessionState);
  const [payload, setPayload] = useRecoilState(rewindConfirmState);
  const [full, setFull] = useState(false);

  useEffect(() => {
    setFull(payload?.mode_default === 'full');
  }, [payload]);

  if (!payload) return null;

  const respond = (action: 'confirm' | 'cancel') => {
    session?.socket?.emit('rewind_confirm_response', {
      form_id: payload.form_id,
      message_id: payload.message_id,
      action,
      rewind_execution_record: full
    });
    setPayload(null);
  };

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) respond('cancel');
      }}
    >
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Rewind operation state</DialogTitle>
          <DialogDescription>{payload.summary}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-700 dark:text-amber-300">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{payload.warning}</span>
          </div>
          <div className="flex items-center justify-between gap-4 rounded-md border border-border p-3">
            <div>
              <Label htmlFor="rewind-full" className="text-sm font-medium">
                Also roll back the execution record
              </Label>
              <p className="mt-1 text-xs text-muted-foreground">
                Chat and plan always reset to this point. Off (recommended): keep executed commands,
                footprints, and terminal history, and preserve cleanup tasks. On: also roll those back.
              </p>
            </div>
            <Switch id="rewind-full" checked={full} onCheckedChange={setFull} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => respond('cancel')}>
            Cancel
          </Button>
          <Button onClick={() => respond('confirm')}>Rewind</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
